/**
 * RankingScreen - ranking global, paginado, com a linha do jogador destacada.
 *
 * Abre pelo evento 'ui:openRanking' do ctx.events (o Lobby e o Scoreboard
 * emitem esse nome) e tambem por chamada direta de open(). Fecha com Esc ou com
 * o botao VOLTAR.
 *
 * Os dados vem de ctx.session.api.ranking({ limit, offset }) - contrato do
 * cabecalho de src/services/Api.js. Cada linha traz:
 *   { userId, nickname, name, matches, wins, kills, deaths, bestScore,
 *     totalScore, kd, position }
 * e a resposta traz { rows, total, limit, offset } JA saneados pelo servidor -
 * por isso a paginacao usa o limit/offset que VOLTOU, e nao o que foi pedido:
 * se o servidor apertou o teto, paginar pelo pedido pularia ou repetiria linhas.
 *
 * TRES ESTADOS VISIVEIS, nao um: CARREGANDO, VAZIO e ERRO (com o texto do
 * ApiError e um botao para tentar de novo). Lista que so trata o caminho feliz e
 * o defeito mais comum de tela de lista - o jogador fica olhando um retangulo
 * vazio sem saber se falhou ou se acabou.
 *
 * Padrao do projeto: constructor(ctx) -> async init() -> open()/close() ->
 * dispose(). Sem update(): a tela nao anima nada por frame.
 *
 * ACENTUACAO: identificador e comentario em ASCII; TEXTO QUE O JOGADOR LE leva
 * acento correto por escape unicode, como no Menu.js.
 */

/** Linhas por pagina. */
const PAGE_SIZE = 25;

/** Nome do evento que abre esta tela (emitido pelo Lobby e pelo Scoreboard). */
const OPEN_EVENT = 'ui:openRanking';

function toInt(value) {
  return Number.isFinite(value) ? Math.trunc(value) : 0;
}

function intText(value) {
  return String(toInt(value));
}

/**
 * K/D. O servidor ja manda calculado (`kd`), mas o fallback existe porque a
 * coluna nao pode virar "undefined" na tela se um dia a resposta vier sem ele.
 */
function kdText(row) {
  const kd = Number.isFinite(row?.kd) ? row.kd : toInt(row?.kills) / Math.max(1, toInt(row?.deaths));
  return (Math.round(kd * 100) / 100).toFixed(2);
}

/** Classe da celula pelo papel dela. Os nomes sb-* sao o contrato do netui.css. */
function classOf(role) {
  if (role === 'place') return 'sb-place net-c-pos';
  if (role === 'name') return 'sb-name net-c-name';
  return 'sb-num net-c-num';
}

/**
 * Colunas do ranking. `optional: true` marca as que somem em tela estreita
 * (regra .net-opt do netui.css): VITORIAS e MORTES sao as menos importantes para
 * decidir quem esta na frente, entao sao elas que saem no celular.
 */
const COLUMNS = [
  {
    role: 'place',
    head: 'POSI\u00c7\u00c3O',
    value: (row, index, offset) => {
      const pos = toInt(row?.position);
      return `#${pos > 0 ? pos : offset + index + 1}`;
    }
  },
  { role: 'name', head: 'APELIDO', value: (row) => String(row?.nickname || '--') },
  { role: 'num', head: 'PARTIDAS', value: (row) => intText(row?.matches) },
  { role: 'num', head: 'VIT\u00d3RIAS', optional: true, value: (row) => intText(row?.wins) },
  { role: 'num', head: 'BAIXAS', value: (row) => intText(row?.kills) },
  { role: 'num', head: 'MORTES', optional: true, value: (row) => intText(row?.deaths) },
  { role: 'num', head: 'K/D', value: (row) => kdText(row) },
  { role: 'num', head: 'PONTOS', value: (row) => intText(row?.totalScore) }
];

function cellClass(col) {
  return classOf(col.role) + (col.optional ? ' net-opt' : '');
}

export class RankingScreen {
  constructor(ctx) {
    this.ctx = ctx;

    this.root = null;
    this._open = false;
    this._loading = false;
    this._page = 0;
    this._pageSize = PAGE_SIZE;
    this._total = 0;
    /** @type {Array<object>} */
    this._rows = [];
    /** @type {object|null} linha do proprio jogador (vem de /api/ranking/me) */
    this._meRow = null;
    /**
     * Contador de requisicao. Duas paginas pedidas em sequencia podem responder
     * fora de ordem; sem este contador a resposta velha sobrescreve a nova e a
     * tela mostra a pagina errada.
     */
    this._reqId = 0;
    this._warned = new Set();
    this._offs = [];

    this._build();
  }

  async init() {
    const events = this.ctx?.events;
    if (events?.on) {
      this._offs.push(events.on(OPEN_EVENT, () => this.open()));

      const authChanged = this.ctx?.EVENTS?.AUTH_CHANGED;
      if (authChanged) {
        // Trocou de conta com a tela aberta: o destaque e a linha fixa do rodape
        // passam a ser de outro jogador.
        this._offs.push(events.on(authChanged, () => this._onAuthChanged()));
      }
    } else {
      this._warnOnce('events', new Error('ctx.events indisponivel'));
    }

    this._onKeyDown = (event) => {
      if (!this._open || event.code !== 'Escape') return;
      event.preventDefault();
      this.close();
    };
    window.addEventListener('keydown', this._onKeyDown);
  }

  open() {
    if (!this.root) return;
    this._open = true;
    this.root.hidden = false;
    this.root.classList.add('open');
    this._page = 0;
    void this._load();
  }

  close() {
    this._open = false;
    // Invalida requisicao em voo: fechada a tela, resposta que chegar depois nao
    // pode reabrir estado nenhum.
    this._reqId++;
    this._loading = false;
    this.root?.classList?.remove?.('open');
    if (this.root) this.root.hidden = true;
  }

  dispose() {
    for (const off of this._offs) {
      try { off?.(); } catch (err) { this._warnOnce('off', err); }
    }
    this._offs.length = 0;
    if (this._onKeyDown) window.removeEventListener('keydown', this._onKeyDown);
    this.root?.remove?.();
    this.root = null;
    this._rows.length = 0;
    this._meRow = null;
  }

  /* ===================================================================== */
  /* carga                                                                  */
  /* ===================================================================== */

  _api() {
    return this.ctx?.session?.api || this.ctx?.api || null;
  }

  _meId() {
    const raw = this.ctx?.session?.user?.id;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
  }

  async _load() {
    const api = this._api();
    if (typeof api?.ranking !== 'function') {
      this._showError('O SERVI\u00c7O DE RANKING N\u00c3O EST\u00c1 DISPON\u00cdVEL.');
      return;
    }

    const id = ++this._reqId;
    this._loading = true;
    this._showLoading();

    let data = null;
    try {
      data = await api.ranking({ limit: this._pageSize, offset: this._page * this._pageSize });
    } catch (err) {
      if (id !== this._reqId || !this._open) return;
      this._loading = false;
      // `message` do ApiError ja e texto em portugues pronto para a tela.
      this._showError(err?.message || 'N\u00c3O FOI POSS\u00cdVEL CARREGAR O RANKING.');
      return;
    }

    if (id !== this._reqId || !this._open) return;
    this._loading = false;

    const rows = Array.isArray(data?.rows) ? data.rows : [];
    const limit = toInt(data?.limit);
    const offset = toInt(data?.offset);
    if (limit > 0) {
      this._pageSize = limit;
      this._page = Math.floor(offset / limit);
    }
    this._total = Number.isFinite(data?.total) ? Math.max(0, Math.trunc(data.total)) : offset + rows.length;
    this._rows = rows;

    this._render();
    await this._loadMe(id);
  }

  /**
   * Linha do proprio jogador, para o rodape fixo. Falhar aqui NAO invalida a
   * lista: o ranking continua na tela e so o rodape deixa de aparecer - ranking
   * inteiro apagado por causa de uma consulta acessoria seria pior.
   */
  async _loadMe(id) {
    const api = this._api();
    if (!this.ctx?.session?.isLoggedIn || typeof api?.myRanking !== 'function') {
      this._meRow = null;
      this._renderMe();
      return;
    }

    try {
      const row = await api.myRanking();
      if (id !== this._reqId || !this._open) return;
      this._meRow = row || null;
    } catch (err) {
      if (id !== this._reqId || !this._open) return;
      this._meRow = null;
      this._warnOnce('myRanking', err);
    }
    this._renderMe();
  }

  _onAuthChanged() {
    if (!this._open) return;
    this._page = 0;
    void this._load();
  }

  /* ===================================================================== */
  /* estados da tela                                                        */
  /* ===================================================================== */

  _showLoading() {
    this.loading.hidden = false;
    this.errorBox.hidden = true;
    this.empty.hidden = true;
    this.table.hidden = true;
    this.meBox.hidden = true;
    this._syncPager(true);
  }

  _showError(message) {
    this.errorText.textContent = String(message || '');
    this.errorBox.hidden = false;
    this.loading.hidden = true;
    this.empty.hidden = true;
    this.table.hidden = true;
    this.meBox.hidden = true;
    this._syncPager(true);
  }

  _render() {
    this.loading.hidden = true;
    this.errorBox.hidden = true;

    const rows = this._rows;
    const empty = rows.length === 0;
    this.empty.hidden = !empty;
    this.table.hidden = empty;

    if (empty) {
      // Pagina vazia limpa a tabela: linha da pagina anterior escondida atras do
      // aviso de "nenhuma partida" reaparece na proxima carga bem-sucedida.
      this.tbody.replaceChildren();
    } else {
      const offset = this._page * this._pageSize;
      const meId = this._meId();
      const trs = this.tbody.children;

      for (let i = 0; i < rows.length; i++) {
        let tr = trs[i];
        if (!tr) {
          tr = document.createElement('tr');
          for (const col of COLUMNS) {
            const td = document.createElement('td');
            td.className = cellClass(col);
            tr.appendChild(td);
          }
          this.tbody.appendChild(tr);
        }
        const row = rows[i];
        for (let c = 0; c < COLUMNS.length; c++) {
          tr.children[c].textContent = COLUMNS[c].value(row, i, offset);
        }
        tr.classList.toggle('net-rank-me', meId !== null && toInt(row?.userId) === meId);
      }

      while (trs.length > rows.length) this.tbody.removeChild(trs[trs.length - 1]);
    }

    this._syncPager(false);
    this._renderMe();
  }

  /**
   * Rodape fixo com a linha do jogador. Ranking em que o jogador nao se acha nao
   * serve para nada: se ele nao esta na pagina visivel, a linha dele aparece
   * aqui embaixo, com a posicao real.
   */
  _renderMe() {
    const meId = this._meId();
    const row = this._meRow;

    if (!row || meId === null || this.table.hidden) {
      this.meBox.hidden = true;
      return;
    }

    const onPage = this._rows.some((r) => toInt(r?.userId) === meId);
    if (onPage) {
      this.meBox.hidden = true;
      return;
    }

    for (let c = 0; c < COLUMNS.length; c++) {
      // index 0 e offset 0: a posicao dessa linha vem de row.position, que a rota
      // /api/ranking/me devolve calculada sobre a tabela inteira.
      this.meCells[c].textContent = COLUMNS[c].value(row, 0, 0);
    }
    this.meBox.hidden = false;
  }

  _syncPager(disabled) {
    const size = this._pageSize > 0 ? this._pageSize : PAGE_SIZE;
    const pages = Math.max(1, Math.ceil(this._total / size));
    const page = Math.min(this._page, pages - 1);

    this.pageBadge.textContent = `P\u00c1GINA ${page + 1} DE ${pages}`;
    this.prevButton.disabled = disabled || this._loading || page <= 0;
    this.nextButton.disabled = disabled || this._loading || page >= pages - 1;
  }

  _go(delta) {
    if (this._loading) return;
    const size = this._pageSize > 0 ? this._pageSize : PAGE_SIZE;
    const pages = Math.max(1, Math.ceil(this._total / size));
    const next = Math.min(Math.max(0, this._page + delta), pages - 1);
    if (next === this._page) return;
    this._page = next;
    void this._load();
  }

  /* ===================================================================== */
  /* construcao do DOM                                                      */
  /* ===================================================================== */

  _build() {
    const root = document.createElement('div');
    root.className = 'net-screen';
    root.hidden = true;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-labelledby', 'ranking-title');

    const panel = document.createElement('div');
    panel.className = 'net-panel wide';
    root.appendChild(panel);

    const eyebrow = document.createElement('div');
    eyebrow.className = 'net-sub';
    eyebrow.textContent = 'DESEMPENHO ACUMULADO';
    panel.appendChild(eyebrow);

    const title = document.createElement('h2');
    title.id = 'ranking-title';
    title.className = 'net-title';
    title.textContent = 'RANKING';
    panel.appendChild(title);

    /* --- carregando ------------------------------------------------------ */
    this.loading = document.createElement('div');
    this.loading.className = 'net-loading';
    this.loading.setAttribute('role', 'status');
    this.loading.textContent = 'CARREGANDO...';
    this.loading.hidden = true;
    panel.appendChild(this.loading);

    /* --- erro ------------------------------------------------------------ */
    this.errorBox = document.createElement('div');
    this.errorBox.hidden = true;
    panel.appendChild(this.errorBox);

    this.errorText = document.createElement('div');
    this.errorText.className = 'net-error';
    this.errorText.setAttribute('role', 'alert');
    this.errorBox.appendChild(this.errorText);

    const errorActions = document.createElement('div');
    errorActions.className = 'net-actions';
    this.errorBox.appendChild(errorActions);

    this.retryButton = this._button('TENTAR DE NOVO', 'btn primary', () => void this._load());
    errorActions.appendChild(this.retryButton);

    /* --- vazio ----------------------------------------------------------- */
    this.empty = document.createElement('div');
    this.empty.className = 'net-empty';
    this.empty.textContent = 'Nenhuma partida registrada ainda.';
    this.empty.hidden = true;
    panel.appendChild(this.empty);

    /* --- tabela ---------------------------------------------------------- */
    this.table = document.createElement('table');
    this.table.className = 'net-table';
    this.table.hidden = true;
    panel.appendChild(this.table);

    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    for (const col of COLUMNS) {
      const th = document.createElement('th');
      th.scope = 'col';
      th.className = cellClass(col);
      th.textContent = col.head;
      headRow.appendChild(th);
    }
    thead.appendChild(headRow);
    this.table.appendChild(thead);

    this.tbody = document.createElement('tbody');
    this.table.appendChild(this.tbody);

    /* --- linha fixa do jogador ------------------------------------------- */
    this.meBox = document.createElement('div');
    this.meBox.className = 'net-rank-fixed';
    this.meBox.hidden = true;
    panel.appendChild(this.meBox);

    const meCaption = document.createElement('div');
    meCaption.className = 'net-sub';
    meCaption.textContent = 'SUA POSI\u00c7\u00c3O';
    this.meBox.appendChild(meCaption);

    // Tabela propria com as MESMAS classes de coluna: com table-layout fixed a
    // largura vem das classes das celulas, entao as duas tabelas ficam alinhadas
    // e escondem as mesmas colunas em tela estreita.
    const meTable = document.createElement('table');
    meTable.className = 'net-table';
    this.meBox.appendChild(meTable);

    const meBody = document.createElement('tbody');
    const meRow = document.createElement('tr');
    meRow.className = 'net-rank-me';
    this.meCells = [];
    for (const col of COLUMNS) {
      const td = document.createElement('td');
      td.className = cellClass(col);
      meRow.appendChild(td);
      this.meCells.push(td);
    }
    meBody.appendChild(meRow);
    meTable.appendChild(meBody);

    /* --- paginacao e saida ----------------------------------------------- */
    const pager = document.createElement('div');
    pager.className = 'net-actions';
    panel.appendChild(pager);

    this.prevButton = this._button('ANTERIOR', 'btn', () => this._go(-1));
    pager.appendChild(this.prevButton);

    this.nextButton = this._button('PR\u00d3XIMA', 'btn', () => this._go(1));
    pager.appendChild(this.nextButton);

    this.pageBadge = document.createElement('span');
    this.pageBadge.className = 'net-badge';
    this.pageBadge.textContent = 'P\u00c1GINA 1 DE 1';
    pager.appendChild(this.pageBadge);

    const actions = document.createElement('div');
    actions.className = 'net-actions';
    panel.appendChild(actions);

    this.backButton = this._button('VOLTAR', 'btn ghost', () => this.close());
    actions.appendChild(this.backButton);

    (document.getElementById('ui-root') || document.body).appendChild(root);
    this.root = root;
  }

  _button(label, cls, onClick) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = cls;
    b.textContent = label;
    b.addEventListener('click', onClick);
    return b;
  }

  _warnOnce(key, err) {
    if (this._warned.has(key)) return;
    this._warned.add(key);
    console.warn(`[RankingScreen] falha em "${key}" - seguindo sem isso:`, err);
  }
}
